/**
 * NoteBlurBridge - Invisible helper that fires `onBlur` when keyboard focus
 * leaves the PARENT element entirely (not when it merely moves between the
 * parent's children).
 *
 * Why this exists: the checklist note fields use the AutoGrowTextarea atom,
 * which we deliberately keep unaware of "save on blur" semantics. Rather than
 * teach the atom about it, we drop this zero-footprint sibling into the same
 * container; it attaches a single `focusout` listener on its parent and only
 * calls back once focus has genuinely left the group. This keeps the row
 * self-contained without modifying the atom.
 *
 * Previously this exact helper was re-declared inline inside both the
 * InspectionChecklist and InspectionWorkflowChecklist organisms — extracted
 * here so the two flows share one implementation.
 *
 * @example
 * <div>
 *   <AutoGrowTextarea ... />
 *   <NoteBlurBridge onBlur={saveDraft} />
 * </div>
 */
import React, { useEffect, useRef } from 'react';

export interface NoteBlurBridgeProps {
	/** Called once focus leaves the parent element entirely. */
	onBlur: () => void;
	/** Optional extra classes (rarely needed — the element is visually hidden). */
	className?: string;
}

export const NoteBlurBridge: React.FC<NoteBlurBridgeProps> = ({ onBlur, className }) => {
	const ref = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const host = ref.current;
		if (!host) return;
		const parent = host.parentElement;
		if (!parent) return;
		const handler = (e: FocusEvent) => {
			// Only react when focus leaves the parent entirely (not when it
			// moves between children of the parent).
			const next = e.relatedTarget as Node | null;
			if (parent.contains(next)) return;
			onBlur();
		};
		parent.addEventListener('focusout', handler);
		return () => parent.removeEventListener('focusout', handler);
	}, [onBlur]);

	return <div ref={ref} className={`note-blur-bridge hidden ${className ?? ''}`} data-ref="molecule-note-blur-bridge" aria-hidden="true" />;
};

export default NoteBlurBridge;
